import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import * as discussionActions from '../../../actions/discussionActions';
import { DISCUSSION_TABS_CONSTANTS, DRAWER_CONTENTS, ROLE } from '../../../const';
import DiscussionListContainer from './DiscussionsListContainer';
import * as drawerActions from '../../../actions/drawerActions';
import * as accountActions from '../../../actions/accountActions';
import * as discussionDrawerActions from '../../../actions/discussionDrawerActions';
import * as classroomGroupsActions from '../../../actions/classroom/classroomGroupActions';
import { getClassId } from '../../../listeners/route/routeUtils';
import CustomIcon from '../../common/CustomIcon';
import { MODES } from '../../drawer-contents/AssignmentsContentPanel/assignmentsContentPanelService';
import { I18n } from 'react-redux-i18n-lite';
import { Chip, EmptyState, PillButton, Popover, SegmentedControl } from '@netexknowledge/smart-components';
import { SmartButton } from '../../common/SmartButton';
import DiscussionsHelp from './DiscussionsHelp';

export class DiscussionsWrapper extends React.Component {
  constructor(props, context) {
    super(props, context);
    this.state = {
      selectMenuOption: {
        key: DISCUSSION_TABS_CONSTANTS.IN_PROGRESS.key,
        label: DISCUSSION_TABS_CONSTANTS.IN_PROGRESS.label
      },
      activeTypeFilter: 'all'
    };
    this.openCreateDiscussionPanel = this.openCreateDiscussionPanel.bind(this);
    this.showAllTypes = this.showAllTypes.bind(this);
  }

  static propTypes = {
    classroom: PropTypes.object,
    discussionActions: PropTypes.object,
    inClassroom: PropTypes.bool,
    user: PropTypes.object,
    futureDiscussions: PropTypes.object,
    finishedDiscussions: PropTypes.object,
    currentDiscussions: PropTypes.object,
    classroomGroupsActions: PropTypes.object,
    drawerActions: PropTypes.object,
    accountActions: PropTypes.object,
    layoutSettings: PropTypes.object,
    role: PropTypes.string,
    isAppOnline: PropTypes.bool,
    discussionDrawerActions: PropTypes.object,
    emptyImg: PropTypes.string
  };

  getClassroomId() {
    const { classroom } = this.props;
    return classroom.id || getClassId();
  }

  loadDiscussions(classroomId) {
    if (!classroomId) {
      return;
    }

    const { role, classroomGroupsActions, discussionActions } = this.props;

    if (role === ROLE.INSTRUCTOR) {
      classroomGroupsActions.getClassroomGroups(classroomId);
    }
    discussionActions.getCurrentDiscussions(classroomId);
    discussionActions.getFinishedDiscussions(classroomId);
    discussionActions.getFutureDiscussions(classroomId);
  }

  componentDidMount() {
    this.loadDiscussions(this.getClassroomId());
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    const { isAppOnline, accountActions, user, classroom } = this.props;
    const prevClassroomId = prevProps.classroom.id || getClassId();
    const classroomId = classroom.id || getClassId();

    if (classroomId && classroomId !== prevClassroomId) {
      this.loadDiscussions(classroomId);
    }

    if (!isAppOnline) {
      accountActions.redirectToHomeOrClassroom(user._id);
    }
  }

  openCreateDiscussionPanel() {
    const { drawerActions, inClassroom, discussionDrawerActions } = this.props;
    if (inClassroom) {
      discussionDrawerActions.setMode(MODES.CREATE);
      drawerActions.openDrawerAndSetContent(DRAWER_CONTENTS.CREATE_DISCUSSIONS_PANEL);
    }
  }

  // Co-instructors get the teacher texts, but only the instructor can create discussions.
  isStaff = () => [ROLE.INSTRUCTOR, ROLE.COINSTRUCTOR].includes(this.props.role);

  canCreate = () => this.props.role === ROLE.INSTRUCTOR;

  showAllTypes() {
    this.setState({ activeTypeFilter: 'all' });
  }

  getTabDiscussions(tab) {
    const { currentDiscussions, finishedDiscussions, futureDiscussions } = this.props;
    if (tab.key === DISCUSSION_TABS_CONSTANTS.IN_PROGRESS.key) {
      return currentDiscussions;
    }
    if (tab.key === DISCUSSION_TABS_CONSTANTS.FINISHED.key) {
      return finishedDiscussions;
    }
    if (tab.key === DISCUSSION_TABS_CONSTANTS.FUTURE.key) {
      return futureDiscussions;
    }
    return { discussions: [], currentPage: 0, pages: 0, fetching: false };
  }

  isAnyTabFetching(tabKeys) {
    return tabKeys.some(key => this.getTabDiscussions(DISCUSSION_TABS_CONSTANTS[key]).fetching);
  }

  // Every tab the role can see is loaded and empty.
  isClassroomEmpty(tabKeys) {
    return (
      !this.isAnyTabFetching(tabKeys) &&
      tabKeys.every(key => !this.getTabDiscussions(DISCUSSION_TABS_CONSTANTS[key]).discussions.length)
    );
  }

  renderNewDiscussionFab() {
    if (this.canCreate()) {
      return (
        <button className="fab-button" onClick={this.openCreateDiscussionPanel}>
          <CustomIcon name="plus-button" size={24} />
        </button>
      );
    }
  }

  getFilteredDiscussions(discussions) {
    const { activeTypeFilter } = this.state;
    if (activeTypeFilter === 'all') {
      return discussions;
    }
    return discussions.filter(discussion =>
      activeTypeFilter === 'content' ? discussion.activityContext : !discussion.activityContext
    );
  }

  getTypeFilterCounts(discussions) {
    return discussions.reduce(
      (counts, discussion) => {
        const key = discussion.activityContext ? 'content' : 'classroom';
        counts[key] += 1;
        counts.all += 1;
        return counts;
      },
      { all: 0, classroom: 0, content: 0 }
    );
  }

  renderTypeFilters(discussions) {
    const { activeTypeFilter } = this.state;
    const counts = this.getTypeFilterCounts(discussions);
    const filters = [
      { type: 'all', label: I18n.t('discussions.filters.all') },
      { type: 'classroom', label: I18n.t('discussions.filters.classroom') },
      { type: 'content', label: I18n.t('discussions.filters.content') }
    ];

    return (
      <div className="discussion-type-filters">
        {filters.map(filter => {
          const isActive = activeTypeFilter === filter.type;
          return (
            <Chip
              key={filter.type}
              label={filter.label}
              count={counts[filter.type]}
              background={isActive ? '#12abe3' : '#fff'}
              borderColor={isActive ? '#12abe3' : '#e5e5e4'}
              textColor={isActive ? '#fff' : '#666'}
              active={isActive}
              onClick={() => this.setState({ activeTypeFilter: filter.type })}
            />
          );
        })}
      </div>
    );
  }

  renderHelp() {
    return (
      <div className="discussions-help">
        <Popover
          title={I18n.t('discussions.help.title')}
          closeLabel={I18n.t('discussions.help.close')}
          width={390}
          trigger={
            <PillButton
              label={I18n.t('discussions.help.title')}
              icon={<i className="custom-icons-question-mark-circle" />}
            />
          }
        >
          <DiscussionsHelp isStaff={this.isStaff()} compact />
        </Popover>
      </div>
    );
  }

  renderClassroomEmpty() {
    const { emptyImg } = this.props;
    const role = this.isStaff() ? 'staff' : 'student';

    return (
      <div className="discussion-list-container-wrapper">
        <EmptyState
          media={emptyImg ? <img className="discussions-empty-image" src={emptyImg} alt="" /> : null}
          title={I18n.t(`discussions.empty.${role}.title`)}
          description={I18n.t(`discussions.empty.${role}.description`)}
        >
          {this.canCreate() && (
            <SmartButton
              id="discussions-empty-create"
              className="discussions-empty-create"
              onClick={this.openCreateDiscussionPanel}
              success
            >
              <CustomIcon name="plus-button" size={13} />
              {I18n.t('discussions.empty.create')}
            </SmartButton>
          )}
          <DiscussionsHelp isStaff={this.isStaff()} variant="empty" framed />
        </EmptyState>
      </div>
    );
  }

  getDiscussions(tab, tabKeys) {
    const { discussions, currentPage, pages, fetching } = this.getTabDiscussions(tab);
    // An empty tab waits for the other tabs before telling an empty tab from an empty classroom.
    const isFetching = fetching || (!discussions.length && this.isAnyTabFetching(tabKeys));
    // Finished discussions can't be created, so that tab offers no create action at all.
    const canCreateInTab = this.canCreate() && tab.key !== DISCUSSION_TABS_CONSTANTS.FINISHED.key;
    // An empty tab already offers "create" in its empty state: the FAB would duplicate it.
    const isEmptyTab = !isFetching && !discussions.length;

    return (
      <div className="discussion-list-container-wrapper" key={`assignments-${tab.key}`}>
        <DiscussionListContainer
          discussionPages={pages}
          currentPage={currentPage}
          discussions={this.getFilteredDiscussions(discussions)}
          hasTabDiscussions={!!discussions.length}
          isFetching={isFetching}
          openCreateDiscussionPanel={this.openCreateDiscussionPanel}
          activeTypeFilter={this.state.activeTypeFilter}
          canCreate={canCreateInTab}
          onShowAllTypes={this.showAllTypes}
          tab={tab}
        />
        {canCreateInTab && !isEmptyTab && this.renderNewDiscussionFab()}
      </div>
    );
  }
  getUserSettings = () => {
    const { layoutSettings, role } = this.props;
    return role === ROLE.INSTRUCTOR
      ? layoutSettings.teacherSettings
      : role === ROLE.COINSTRUCTOR
      ? layoutSettings.coinstructorSettings
      : layoutSettings.studentSettings;
  };

  handleClickMenuItem = option => {
    this.setState({
      selectMenuOption: {
        key: option.value,
        label: option.name
      }
    });
  };

  render() {
    const userSettings = this.getUserSettings();
    const tabConstants = {};
    userSettings.classroomSections.discussionTabs.forEach(
      tabConst => (tabConstants[tabConst] = DISCUSSION_TABS_CONSTANTS[tabConst])
    );
    const tabKeys = Object.keys(tabConstants);
    const activeTabKey = tabConstants[this.state.selectMenuOption.key] ? this.state.selectMenuOption.key : tabKeys[0];
    const activeTab = DISCUSSION_TABS_CONSTANTS[activeTabKey];
    const { discussions } = this.getTabDiscussions(activeTab);
    const classroomEmpty = this.isClassroomEmpty(tabKeys);

    return (
      <div className="discussion-content-wrapper">
        <div className="discussion-filters-row">
          <div className="discussion-tabs-selector">
            {tabKeys.length > 1 && (
              <SegmentedControl
                segments={tabKeys.map(key => ({ key, label: I18n.t(tabConstants[key].label) }))}
                activeKey={activeTabKey}
                onChange={key =>
                  this.handleClickMenuItem({ value: key, name: I18n.t(DISCUSSION_TABS_CONSTANTS[key].label) })
                }
              />
            )}
            {!classroomEmpty && this.renderHelp()}
          </div>
          {!classroomEmpty && this.renderTypeFilters(discussions)}
        </div>
        {classroomEmpty ? this.renderClassroomEmpty() : this.getDiscussions(activeTab, tabKeys)}
      </div>
    );
  }
}

function mapStateToProps(state) {
  return {
    currentDiscussions: state.discussions.currentDiscussions,
    finishedDiscussions: state.discussions.finishedDiscussions,
    futureDiscussions: state.discussions.futureDiscussions,
    classroom: state.classrooms.selectedClassroom.classroom,
    isAppOnline: state.connection.status.online,
    layoutSettings: state.theme.layoutSettings,
    inClassroom: state.router.inClassroom,
    role: state.classrooms.selectedClassroom.classroom.role,
    user: state.account.user,
    emptyImg: state.theme.images.placeholders.empty
  };
}

function mapDispatchToProps(dispatch) {
  return {
    drawerActions: bindActionCreators(drawerActions, dispatch),
    classroomGroupsActions: bindActionCreators(classroomGroupsActions, dispatch),
    discussionDrawerActions: bindActionCreators(discussionDrawerActions, dispatch),
    discussionActions: bindActionCreators(discussionActions, dispatch),
    accountActions: bindActionCreators(accountActions, dispatch)
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DiscussionsWrapper);
